/**
 * Merge — unione di log di eventi (contratto §2.3 "Merge strategy"; M2: robustezza dati +
 * multi-device, build plan §3). Logica pura = test obbligatorio (regola 8): qui vivono le
 * proprietà che il property test deve dimostrare — dedup per `event_id`, ordine totale D49,
 * convergenza (stessi input → stesso output indipendentemente dall'ordine di arrivo).
 *
 * CONFLITTO (stesso `event_id`, payload diverso = corruzione o log divergenti):
 * - nel MERGE SIMMETRICO vince il payload con serializzazione canonica minima — deterministico
 *   e commutativo (la scelta NON dipende dall'ordine degli input);
 * - nell'IMPORT chi è già nel log di destinazione vince (append-only §2.3) e il conflitto si
 *   RIPORTA nel resoconto — mai silenzioso (regola 1: tutto ispezionabile);
 * - conflitto INTERNO al file importato (entrambi i payload sono nuovi): stessa regola del merge
 *   simmetrico — canonico minimo — così l'esito NON dipende dall'ordine delle righe del file
 *   (blind review M2, MINOR 1: prima vinceva la prima riga, non dichiarato). Si riporta comunque
 *   il CONTEGGIO: mai silenzio.
 *
 * IDENTITÀ (D56, 2026-09-26, voto proprietario — blind review M2 MAJOR 2): un device senza
 * eventi propri ADOPTA l'user_id del file importato (bootstrap dell'identità, §1.1: "la stessa
 * persona su due device è lo stesso utente"). Se esistono già identità in conflitto NON si fonde
 * nulla in silenzio: il conflitto si riporta e "Unisci/Tieni separati" resta al sync M5 (D24).
 */
import { compareEvents, type HyprEvent } from "../storage/events";

/**
 * Serializzazione canonica: stesse chiavi → stessa stringa, indipendentemente dall'ordine di
 * inserimento (JSON.stringify segue l'ordine di inserimento: inaffidabile per confronti).
 */
export function canonico(v: unknown): string {
  if (v === null || typeof v !== "object") return JSON.stringify(v) ?? "null";
  if (Array.isArray(v)) return `[${v.map(canonico).join(",")}]`;
  const chiavi = Object.keys(v as Record<string, unknown>).sort();
  return `{${chiavi.map((k) => `${JSON.stringify(k)}:${canonico((v as Record<string, unknown>)[k])}`).join(",")}}`;
}

/** Payload identici? (confronto canonico: l'ordine delle chiavi non è contenuto) */
export function stessoEvento(a: HyprEvent, b: HyprEvent): boolean {
  return canonico(a) === canonico(b);
}

/**
 * Unione di liste di eventi: dedup per `event_id` (§2.3) + ordine totale D49. Idempotente,
 * commutativa e associativa per costruzione (property test in tests/merge.test.ts):
 * - dedup: un evento duplicato (stesso id, stesso payload) compare UNA volta;
 * - conflitto (stesso id, payload diverso): vince il canonico minimo (deterministico).
 */
export function unioneEventi(...liste: HyprEvent[][]): HyprEvent[] {
  const perId = new Map<string, HyprEvent>();
  for (const lista of liste) {
    for (const e of lista) {
      const presente = perId.get(e.event_id);
      if (presente === undefined || canonico(e) < canonico(presente)) perId.set(e.event_id, e);
    }
  }
  return [...perId.values()].sort(compareEvents);
}

export interface EsitoImport {
  /** eventi MAI visti nel log di destinazione → da appendere */
  nuovi: HyprEvent[];
  /** già presenti con lo STESSO payload → scartati senza danno (dedup §2.3) */
  duplicati: number;
  /** già presenti con payload DIVERSO → NON importati (vince il log di destinazione, append-only) */
  conflitti: HyprEvent[];
  /** stesso id con payload DIVERSO DENTRO il file: importato il canonico minimo (contati, mai silenzio) */
  conflittiInterni: number;
}

export type EsitoIdentita =
  /** nessun evento con `user_id` nel file: nessuna identità di cui occuparsi */
  | { tipo: "nessuna" }
  /** il device non ha eventi propri → ADOPTAtta l'identità del file (D56, bootstrap §1.1) */
  | { tipo: "adotta"; user_id: string }
  /** è già l'identità del file: nessuna azione da fare */
  | { tipo: "allineata"; user_id: string }
  /** identità in conflitto (locali e/o multiple nel file): NO fusione silenziosa — decide M5 (D24) */
  | { tipo: "conflitto"; identita: string[] };

/**
 * Identità dell'import (D56): `locale` è lo `user_id` del device (`null` se ancora non nato).
 * Regola deterministica, in ordine:
 * 1. il file porta 0 o più di 1 `user_id` → `conflitto` (identità multiple: decide M5, D24);
 * 2. il device non ha eventi con la PROPRIA identità e il log locale contiene solo l'identità
 *    del file → `adotta` (bootstrap: §1.1 vieta l'identità "unico per device");
 * 3. è già l'identità del file → `allineata`;
 * 4. altrimenti → `conflitto` (mai fusione silenziosa).
 */
export function esitoIdentita(file: HyprEvent[], locali: HyprEvent[], locale: string | null): EsitoIdentita {
  const idFile = [...new Set(file.map((e) => e.user_id))].sort();
  if (idFile.length !== 1) return idFile.length === 0 ? { tipo: "nessuna" } : { tipo: "conflitto", identita: idFile };
  const u = idFile[0] as string;
  const idLocali = [...new Set(locali.map((e) => e.user_id))].sort();
  const propri = locale === null ? [] : locali.filter((e) => e.user_id === locale);
  if (propri.length === 0 && idLocali.every((x) => x === u)) return { tipo: "adotta", user_id: u };
  if (locale === u) return { tipo: "allineata", user_id: u };
  return { tipo: "conflitto", identita: [...new Set([...idLocali, ...idFile])].sort() };
}

/**
 * Cosa importare in un log già popolato: l'import è un append con dedup per `event_id` —
 * l'append-only non si tocca mai, i duplicati non si duplicano, i conflitti non si risolvono
 * in silenzio (§2.3 + regola 1).
 */
export function eventiDaImportare(esistenti: HyprEvent[], importati: HyprEvent[]): EsitoImport {
  const perId = new Map<string, HyprEvent>();
  for (const e of esistenti) if (!perId.has(e.event_id)) perId.set(e.event_id, e);
  const nuovi = new Map<string, HyprEvent>();
  let duplicati = 0;
  let conflittiInterni = 0;
  const conflitti: HyprEvent[] = [];
  for (const e of importati) {
    const nelDest = perId.get(e.event_id);
    if (nelDest !== undefined) {
      if (stessoEvento(nelDest, e)) duplicati += 1;
      else conflitti.push(e);
      continue;
    }
    const gia = nuovi.get(e.event_id);
    if (gia === undefined) {
      nuovi.set(e.event_id, e);
      continue;
    }
    if (stessoEvento(gia, e)) {
      duplicati += 1; // doppione identico DENTRO il file: dedup §2.3, non un conflitto
      continue;
    }
    // conflitto INTERNO al file (entrambi nuovi, payload DIVERSI): canonico minimo, come
    // unioneEventi — deterministico e indipendente dall'ordine delle righe (contato: mai silenzio)
    conflittiInterni += 1;
    if (canonico(e) < canonico(gia)) nuovi.set(e.event_id, e);
  }
  return { nuovi: [...nuovi.values()].sort(compareEvents), duplicati, conflitti, conflittiInterni };
}
